// Query hooks for the customer dashboard. Every call goes to /api/v1 on this
// Worker, which adds the session.
import { useQuery } from '@tanstack/react-query';
import { ApiError, createApi, ok } from '@cover/core/api/client.ts';
import type { FactField, Quote } from '@cover/core/api/types.ts';
import { useBusinessId } from './session.tsx';

export const api = createApi();

export const keys = {
  me: ['me'] as const,
  profile: (b: string) => ['profile', b] as const,
  facts: (b: string) => ['facts', b] as const,
  fields: ['fact-fields'] as const,
  assets: (b: string) => ['assets', b] as const,
  summary: (b: string) => ['asset-summary', b] as const,
  documents: (b: string) => ['documents', b] as const,
  policies: (b: string) => ['policies', b] as const,
  quotes: (b: string) => ['quotes', b] as const,
  claims: (b: string) => ['claims', b] as const,
  claim: (id: string) => ['claim', id] as const,
  connections: (b: string) => ['connections', b] as const,
  referral: ['my-referral'] as const,
  next: ['me-next'] as const,
  payment: ['payment-method'] as const,
  apps: ['app-connections'] as const,
};

/** What to do next for where this customer is (GET /me/next). */
export function useNext() {
  return useQuery({ queryKey: keys.next, queryFn: () => ok(api.GET('/me/next')) });
}

/** The demo card on file, or null when there isn't one (404). */
export function usePaymentMethod() {
  return useQuery({
    queryKey: keys.payment,
    queryFn: () =>
      ok(api.GET('/me/payment-method')).catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
  });
}

/** Claude / ChatGPT connected to this account. */
export function useAppConnections() {
  return useQuery({ queryKey: keys.apps, queryFn: () => ok(api.GET('/me/app-connections')).then((r) => r.data) });
}

export function useProfile() {
  const b = useBusinessId();
  return useQuery({ queryKey: keys.profile(b), queryFn: () => ok(api.GET('/businesses/{businessId}', { params: { path: { businessId: b } } })) });
}

/** Proposed facts aren't "current", so they come from a second call. */
export function useProposedFacts() {
  const b = useBusinessId();
  return useQuery({
    queryKey: [...keys.facts(b), 'proposed'],
    queryFn: () => ok(api.GET('/businesses/{businessId}/facts', { params: { path: { businessId: b }, query: { status: 'proposed' } } })).then((r) => r.data),
  });
}

export function useFields() {
  return useQuery({
    queryKey: keys.fields,
    queryFn: () => ok(api.GET('/fact-fields')).then((r) => r.data),
    staleTime: Infinity,
    select: (data) => new Map<string, FactField>(data.map((f) => [f.field, f])),
  });
}

export function useAssets() {
  const b = useBusinessId();
  return useQuery({ queryKey: keys.assets(b), queryFn: () => ok(api.GET('/businesses/{businessId}/assets', { params: { path: { businessId: b }, query: { limit: 200 } } })).then((r) => r.data) });
}

export function useAssetSummary() {
  const b = useBusinessId();
  return useQuery({ queryKey: keys.summary(b), queryFn: () => ok(api.GET('/businesses/{businessId}/assets/summary', { params: { path: { businessId: b } } })) });
}

const PROCESSING = new Set(['received', 'scanning', 'classifying']);

export function useDocuments() {
  const b = useBusinessId();
  return useQuery({
    queryKey: keys.documents(b),
    queryFn: () => ok(api.GET('/businesses/{businessId}/documents', { params: { path: { businessId: b }, query: { limit: 200 } } })).then((r) => r.data),
    // Uploads are classified in the background; poll until they settle.
    refetchInterval: (q) => (q.state.data?.some((d) => PROCESSING.has(d.status)) ? 2000 : false),
  });
}

/** Every quote version (grouped by `reference` on the Quotes page). */
export function useQuotes() {
  const b = useBusinessId();
  return useQuery({
    queryKey: keys.quotes(b),
    queryFn: () => ok(api.GET('/businesses/{businessId}/quotes', { params: { path: { businessId: b } } })).then((r) => r.data as Quote[]),
  });
}

export function usePolicies() {
  const b = useBusinessId();
  return useQuery({ queryKey: keys.policies(b), queryFn: () => ok(api.GET('/businesses/{businessId}/policies', { params: { path: { businessId: b } } })).then((r) => r.data) });
}

export function useClaims() {
  const b = useBusinessId();
  return useQuery({ queryKey: keys.claims(b), queryFn: () => ok(api.GET('/claims', { params: { query: { businessId: b } } })).then((r) => r.data) });
}

export function useClaim(id: string) {
  return useQuery({ queryKey: keys.claim(id), queryFn: () => ok(api.GET('/claims/{claimId}', { params: { path: { claimId: id } } })) });
}

export function useConnections() {
  const b = useBusinessId();
  return useQuery({ queryKey: keys.connections(b), queryFn: () => ok(api.GET('/businesses/{businessId}/connections', { params: { path: { businessId: b } } })).then((r) => r.data) });
}

export function useMyReferral() {
  return useQuery({ queryKey: keys.referral, queryFn: () => ok(api.GET('/me/referral')) });
}

/** cover-connect's OAuth start URL for FreeAgent or Xero, with an engine-signed state. */
export function startOAuth(businessId: string, provider: 'freeagent' | 'xero', returnTo: string) {
  return ok(api.POST('/businesses/{businessId}/connections/{provider}/authorize', { params: { path: { businessId, provider } }, body: { returnTo } }));
}
